import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CheckCircle2, Pause, Pencil, Play, Repeat, Trash2, XCircle, Zap } from "lucide-react";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { TaskActionButton } from "@/components/agent/task-action-button";
import { useCanManageAgent } from "@/hooks/use-agent-access";
import { Badge } from "@/components/ui/badge";
import { cronToText, timezoneLabel } from "@/lib/cron-text";
import {
  TASK_STATE_LABEL,
  TASK_STATE_VARIANT,
  nextRunOf,
  relativeTime,
  taskState,
} from "@/lib/task-display";

/**
 * The one task list, used by the top-bar Tasks view, the settings popup's
 * Tasks page and the agent settings Tasks tab. Same row, same columns, same
 * actions everywhere; the popup only adds the agent's name under the title.
 *
 * A row holds what you scan for: kind, name, status, schedule, last and next
 * run, and actions. The description, prompt, destination and last result live
 * in the drawer that clicking a row opens.
 */

/** Every task query starts with this, so one invalidation refreshes all views. */
export const TASKS_QUERY_ROOT = ["agent-tasks"] as const;

export function TaskList({
  tasks,
  showAgent = false,
  onOpen,
  emptyText = "No tasks yet.",
}: {
  tasks: AgentTask[];
  /** Show the agent's name under each title (the all-agents view). */
  showAgent?: boolean;
  onOpen: (task: AgentTask) => void;
  emptyText?: string;
}) {
  if (tasks.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface px-4 py-3 text-xs text-text-muted">
        {emptyText}
      </div>
    );
  }
  const recurring = tasks.filter((t) => t.type === "recurring");
  const oneOff = tasks.filter((t) => t.type === "one_off");
  return (
    <div className="space-y-4">
      {recurring.length > 0 && (
        <TaskGroup label="Recurring" tasks={recurring} showAgent={showAgent} onOpen={onOpen} />
      )}
      {oneOff.length > 0 && (
        <TaskGroup label="One-Off" tasks={oneOff} showAgent={showAgent} onOpen={onOpen} />
      )}
    </div>
  );
}

function TaskGroup({
  label,
  tasks,
  showAgent,
  onOpen,
}: {
  label: string;
  tasks: AgentTask[];
  showAgent: boolean;
  onOpen: (task: AgentTask) => void;
}) {
  return (
    <section>
      <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
        {label} <span className="font-normal opacity-70">· {tasks.length}</span>
      </p>
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} showAgent={showAgent} onOpen={() => onOpen(task)} />
        ))}
      </div>
    </section>
  );
}

function TaskRow({
  task,
  showAgent,
  onOpen,
}: {
  task: AgentTask;
  showAgent: boolean;
  onOpen: () => void;
}) {
  const queryClient = useQueryClient();
  // Run, Pause/Resume, Edit and Delete: tenant admins, and agent admins for
  // agents they manage. A standard user only views (Kenneth, 2026-10-08);
  // clicking the row still opens the task to read.
  const canManage = useCanManageAgent()(task.agentId);
  const refresh = () => queryClient.invalidateQueries({ queryKey: TASKS_QUERY_ROOT });

  const trigger = useMutation({ mutationFn: () => agentTasksApi.trigger(task.id), onSuccess: refresh });
  const toggle = useMutation({
    mutationFn: () =>
      agentTasksApi.update(task.id, {
        enabled: !task.enabled,
        status: task.enabled ? "paused" : "active",
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => agentTasksApi.delete(task.id), onSuccess: refresh });

  const state = taskState(task);
  const recurring = task.type === "recurring";
  const next = nextRunOf(task);
  const schedule = recurring
    ? `${cronToText(task.cronExpression)}${task.timezone && task.timezone !== "UTC" ? ` (${timezoneLabel(task.timezone)})` : " (UTC)"}`
    : "One-off";
  const lastOk = task.lastResult?.success;

  return (
    <div
      onClick={onOpen}
      className="grid cursor-pointer grid-cols-[20px_minmax(0,1fr)_76px_minmax(0,190px)_84px_64px_120px] items-center gap-3 border-b border-border px-4 py-2.5 transition-colors last:border-0 hover:bg-surface-hover"
    >
      {recurring ? (
        <Repeat size={14} className="text-purple-400" />
      ) : (
        <CalendarClock size={14} className="text-blue-400" />
      )}

      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-text-primary">{task.title}</p>
        {showAgent && task.agentName && (
          <p className="truncate text-[11px] text-text-muted">{task.agentName}</p>
        )}
      </div>

      <Badge variant={TASK_STATE_VARIANT[state]} className="w-fit text-[10px]">
        {TASK_STATE_LABEL[state]}
      </Badge>

      <span className="truncate text-xs text-text-secondary" title={task.cronExpression ?? undefined}>
        {schedule}
      </span>

      <span className="flex items-center gap-1 text-xs text-text-muted" title="Last run">
        {lastOk === true && <CheckCircle2 size={11} className="shrink-0 text-emerald-400" />}
        {lastOk === false && <XCircle size={11} className="shrink-0 text-red-400" />}
        {task.lastRunAt ? relativeTime(task.lastRunAt) : "Not run"}
      </span>

      <span className="text-xs text-text-muted" title="Next run">
        {next ? relativeTime(next) : "—"}
      </span>

      <div className="flex items-center justify-end gap-0.5">
        {canManage && (<>
        <TaskActionButton
          label={recurring || state === "pending" ? "Run now" : "Run again"}
          onClick={() => trigger.mutate()}
          disabled={trigger.isPending}
        >
          <Zap size={13} />
        </TaskActionButton>
        {recurring && (
          <TaskActionButton
            label={task.enabled ? "Pause" : "Resume"}
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending}
          >
            {task.enabled ? <Pause size={13} /> : <Play size={13} />}
          </TaskActionButton>
        )}
        <TaskActionButton label="Edit" onClick={onOpen}>
          <Pencil size={13} />
        </TaskActionButton>
        <TaskActionButton
          label="Delete"
          tone="danger"
          onClick={() => {
            if (confirm(`Delete task "${task.title}"? This cannot be undone.`)) remove.mutate();
          }}
          disabled={remove.isPending}
        >
          <Trash2 size={13} />
        </TaskActionButton>
        </>)}
      </div>
    </div>
  );
}
